// @ts-nocheck
// client/src/v2/cadernos.tsx
// CADERNOS — a casa das notas.
//
// Substitui o botão "Estudos" da aba Biblioteca do perfil. A visão global de
// antes não some: ela vira o atalho "Todas as notas" aqui dentro. Não é um
// caderno — é uma visão sobre todos eles.
//
// Regra de cor deste arquivo (e de todo componente novo): ZERO cor literal.
// São 4 paletas e DUAS delas são claras; um `#161210` aqui viraria bloco
// escuro sobre papel bege. Só var(--...), e ember é acento, nunca fundo.
//
// Degradação: enquanto a migration não tiver rodado, o servidor responde
// `disponivel: false` e esta tela mostra um aviso sóbrio em vez de erro.

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { api } from '../services/apiV2';
import { Icon } from './icons';
import { semAcento, LABEL, NoteCard, Destacado, makePageLabel } from './note-card';
import { textoPuro } from './doc';
import { NotaEditor } from './nota-editor';
import { NotaComposer, BarraDeCaptura } from './nota-composer';
import { CadernoFolha } from './caderno-folha';

const CARD = {
  background: 'var(--paper-2)',
  border: '1px solid var(--line)',
  borderRadius: 22,
};
// Os mesmos chips da tela antiga, para o vocabulário não mudar no meio do
// caminho. "Foto" não é um `type`: é a presença do arquivo.
const FILTROS_TIPO = [
  { key: 'tudo', label: 'Tudo' }, { key: 'grifo', label: 'Grifos' },
  { key: 'ideia', label: 'Ideias' }, { key: 'pergunta', label: 'Perguntas' },
  { key: 'foto', label: 'Fotos' }, { key: 'audio', label: 'Áudios' },
];
const ORDENS = [
  { key: 'recentes', label: 'Recentes' }, { key: 'pagina', label: 'Página' }, { key: 'titulo', label: 'Título' },
];

const EYEBROW: React.CSSProperties = {
  fontFamily: 'Geist Mono, monospace', fontSize: 9.5, letterSpacing: '0.16em',
  textTransform: 'uppercase', fontWeight: 600, color: 'var(--ink-4)',
};

/** O trecho encontrado, destacado. Trabalha sobre o texto SEM acento para
 *  achar a posição, mas recorta o texto ORIGINAL — senão a nota apareceria
 *  sem acento na tela só porque foi buscada sem. */
/** A prévia mostra o TEXTO, não a marcação — nem `##` da nota antiga, nem
 *  `<b>` da folha do caderno. Quem sabe traduzir os dois é `doc.ts`; aqui só
 *  resta espremer os espaços para caber numa linha. */
function semMarcacao(n: any): string {
  return textoPuro(n).replace(/\s+/g, ' ').trim();
}

/** Um atalho como LADRILHO, lado a lado com os outros.
 *
 *  Eram cinco linhas de largura inteira — quase metade da tela gasta em cinco
 *  botões, e os cadernos, que são o assunto da página, só apareciam depois de
 *  rolar. Lado a lado, os cinco ocupam uma faixa e o caderno sobe.
 *
 *  O NÚMERO ganha o lugar de honra e o rótulo encolhe: quem já conhece a tela
 *  lê o ícone, e quem não conhece lê o rótulo uma vez e nunca mais. Zero sai
 *  apagado — um "0" com o mesmo peso do resto faz a tela parecer cheia de
 *  lugares para ir quando não há nada em nenhum deles. */
function Atalho({ icone, rotulo, nome, total, ativo, onClick }: any) {
  const vazio = !total;
  return (
    // `nome` e o titulo por extenso, `rotulo` e o que cabe no ladrilho. Eram a
    // mesma coisa, e ao encurtar o rotulo o nome inteiro se perdeu junto —
    // quem usa leitor de tela ouvia "Todas", sem dizer todas o que.
    <button onClick={onClick} title={nome || rotulo} aria-label={nome || rotulo} style={{
      flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column',
      alignItems: 'center', gap: 3, padding: '11px 4px 9px',
      borderRadius: 14, cursor: 'pointer',
      background: ativo ? 'var(--ember-soft)' : 'var(--paper-2)',
      border: `1px solid ${ativo ? 'var(--ember)' : 'var(--line)'}`,
      opacity: vazio && !ativo ? 0.62 : 1,
    }}>
      <Icon name={icone} size={17} color={ativo ? 'var(--ember)' : 'var(--ink-3)'} />
      <span className="mono" style={{
        fontSize: 13, lineHeight: 1,
        color: ativo ? 'var(--ember-2)' : vazio ? 'var(--ink-4)' : 'var(--ink)',
      }}>{total}</span>
      <span style={{
        fontSize: 9.5, lineHeight: 1.15, textAlign: 'center',
        color: ativo ? 'var(--ember-2)' : 'var(--ink-4)',
        // Rótulo de duas palavras cabe em duas linhas num ladrilho de ~66px;
        // cortar com reticências esconderia justamente a palavra que
        // diferencia "Para memorizar" de "Fixadas".
        overflowWrap: 'anywhere',
      }}>{rotulo}</span>
    </button>
  );
}

function LinhaCaderno({ c, onAbrir, onMenu }: any) {
  const quando = c.ultimaEdicao
    ? new Date(c.ultimaEdicao).toLocaleDateString('pt-BR', { day: 'numeric', month: 'short' })
    : null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '13px 0', borderBottom: '1px solid var(--line)' }}>
      <button onClick={onAbrir} style={{
        flex: 1, display: 'flex', alignItems: 'center', gap: 12,
        background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left', minWidth: 0,
      }}>
        <span style={{
          width: 34, height: 34, borderRadius: 11, flexShrink: 0,
          background: 'var(--paper-3)', display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Icon name={c.kind === 'livro' ? 'book' : c.kind === 'entrada' ? 'mail' : 'library'} size={15} color="var(--ink-3)" />
        </span>
        <span style={{ minWidth: 0, flex: 1 }}>
          <span style={{
            display: 'block', fontSize: 14.5, color: 'var(--ink)', fontWeight: 600,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {c.pinned && <Icon name="star" size={11} color="var(--ember)" style={{ marginRight: 5 }} />}
            {c.name}
          </span>
          <span style={{ display: 'block', fontSize: 12, color: 'var(--ink-4)', marginTop: 2 }}>
            {/* CADERNO PESSOAL SE CONTA EM PALAVRAS, e não em páginas.
                Enquanto a página era linha no banco, "5 páginas" era um fato.
                Agora ela é vitrine — quantas cabem depende do aparelho e do
                corpo da letra —, e um número que muda sozinho não é contagem.
                As notas de voz e foto que morarem nele aparecem ao lado,
                porque continuam sendo notas. */}
            {[
              c.palavras ? `${c.palavras} ${c.palavras === 1 ? 'palavra' : 'palavras'}` : null,
              c.total ? `${c.total} ${c.total === 1 ? 'nota' : 'notas'}` : null,
            ].filter(Boolean).join(' · ') || 'vazio'}
            {quando ? ` · ${quando}` : ''}
          </span>
        </span>
      </button>
      {onMenu && (
        <button onClick={onMenu} aria-label="Opções" style={{
          background: 'transparent', border: 'none', padding: 6, cursor: 'pointer', color: 'var(--ink-4)',
        }}>⋯</button>
      )}
    </div>
  );
}

export const CadernosScreen: React.FC<{
  session: any;
  onBack: () => void;
  onAbrirLivro?: (bookId: string, page: number | null) => void;
}> = ({ session, onBack, onAbrirLivro }) => {
  const [dados, setDados] = useState<any>(null);
  const [carregando, setCarregando] = useState(true);
  const [busca, setBusca] = useState('');
  const [vista, setVista] = useState<any>({ tipo: 'inicio' });
  const [notas, setNotas] = useState<any[]>([]);
  const [carregandoNotas, setCarregandoNotas] = useState(false);
  const [criando, setCriando] = useState(false);
  const [nomeNovo, setNomeNovo] = useState('');
  const [menuDe, setMenuDe] = useState<any>(null);
  const [renomeando, setRenomeando] = useState<any>(null);
  const [nomeEdit, setNomeEdit] = useState('');
  const [ordem, setOrdem] = useState<'recentes' | 'pagina' | 'titulo'>('recentes');
  const [tipoFiltro, setTipoFiltro] = useState<string>('tudo');
  const [verTodosLivros, setVerTodosLivros] = useState(false);
  const [editando, setEditando] = useState<any>(null);
  const [novoAberto, setNovoAberto] = useState(false);
  const [novoModo, setNovoModo] = useState<'texto' | 'voz' | 'foto'>('texto');
  const [novoTexto, setNovoTexto] = useState('');
  // Abrir um caderno PESSOAL cai direto na folha: abrir um caderno e comecar
  // a escrever nele, nao ler um indice. A lista continua a um toque, no botao
  // do topo da folha.
  const [folha, setFolha] = useState<any>(null);
  const [tags, setTags] = useState<any[]>([]);
  const [menuTag, setMenuTag] = useState<any>(null);
  const [nomeTag, setNomeTag] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  // A busca do INÍCIO também procura no texto das notas (pente fino, UX-01).
  // Antes ela só filtrava nomes de caderno e de livro — e o campo prometia
  // "cadernos e notas". O servidor já busca sem acento (`?busca=`).
  const [achadas, setAchadas] = useState<any[] | null>(null);

  const carregar = useCallback(async () => {
    try {
      const d = await api.listCadernos(session);
      setDados(d);
      try { const t = await api.listarTags(session); setTags(t.tags || []); } catch { setTags([]); }
    } catch (e: any) {
      setErro(e?.message || 'Não deu para carregar os cadernos.');
    } finally {
      setCarregando(false);
    }
  }, [session]);

  useEffect(() => { carregar(); }, [carregar]);

  useEffect(() => {
    const q = busca.trim();
    if (vista.tipo !== 'inicio' || q.length < 2) { setAchadas(null); return; }
    let vivo = true;
    const t = setTimeout(async () => {
      try {
        const r = await api.listarNotas(session, { busca: q });
        if (vivo) setAchadas(r.notas || []);
      } catch { if (vivo) setAchadas([]); }
    }, 300);
    return () => { vivo = false; clearTimeout(t); };
  }, [busca, vista.tipo, session]);

  const abrirVista = useCallback(async (v: any) => {
    setVista(v);
    if (v.tipo === 'inicio') return;
    setCarregandoNotas(true);
    try {
      const filtros: any = {};
      if (v.tipo === 'caderno') filtros.caderno = v.id;
      if (v.tipo === 'fixadas') filtros.fixadas = '1';
      if (v.tipo === 'memorizar') filtros.memorizar = '1';
      if (v.tipo === 'lixeira') filtros.lixeira = '1';
      if (v.tipo === 'tag') filtros.tag = v.id;
      const r = await api.listarNotas(session, filtros);
      setNotas(r.notas || []);
    } catch {
      setNotas([]);
    } finally {
      setCarregandoNotas(false);
    }
  }, [session]);

  const criarCaderno = async () => {
    const nome = nomeNovo.trim();
    if (!nome) return;
    try {
      await api.criarCaderno(session, { name: nome });
      setNomeNovo(''); setCriando(false);
      await carregar();
    } catch (e: any) {
      setErro(e?.message || 'Não deu para criar o caderno.');
    }
  };

  const renomear = async () => {
    const nome = nomeEdit.trim();
    if (!nome || !renomeando) return;
    try {
      await api.editarCaderno(session, renomeando.id, { name: nome });
      setRenomeando(null); setNomeEdit('');
      await carregar();
    } catch (e: any) { setErro(e?.message || 'Não deu para renomear.'); }
  };

  const fixar = async (c: any) => {
    setMenuDe(null);
    try { await api.editarCaderno(session, c.id, { pinned: !c.pinned }); await carregar(); }
    catch (e: any) { setErro(e?.message || 'Não deu para fixar.'); }
  };

  const apagar = async (c: any, destino: 'entrada' | 'lixeira') => {
    setMenuDe(null);
    try { await api.apagarCaderno(session, c.id, destino); await carregar(); }
    catch (e: any) { setErro(e?.message || 'Não deu para apagar.'); }
  };

  const renomearTag = async () => {
    const nome = nomeTag.trim();
    if (!nome || !menuTag) return;
    try {
      await api.renomearTag(session, menuTag.id, nome);
      setMenuTag(null); setNomeTag('');
      await carregar();
    } catch (e: any) { setErro(e?.message || 'Não deu para renomear a etiqueta.'); }
  };

  const apagarTag = async (t: any) => {
    // A etiqueta some da lista e das notas — mas o `#tag` continua escrito no
    // corpo de quem escreveu. Reescrever texto alheio para limpar um índice
    // seria o pior tipo de zelo.
    if (!window.confirm(`Apagar a etiqueta #${t.name}? O texto das notas não muda.`)) return;
    setMenuTag(null);
    try { await api.apagarTag(session, t.id); await carregar(); }
    catch (e: any) { setErro(e?.message || 'Não deu para apagar a etiqueta.'); }
  };

  const restaurar = async (n: any) => {
    try { await api.restaurarNota(session, n.id); await abrirVista(vista); await carregar(); }
    catch (e: any) { setErro(e?.message || 'Não deu para restaurar.'); }
  };

  const esvaziar = async () => {
    if (!window.confirm('Esvaziar a lixeira? As notas e os arquivos de áudio e foto são apagados de vez.')) return;
    try { await api.esvaziarLixeira(session); await abrirVista(vista); await carregar(); }
    catch (e: any) { setErro(e?.message || 'Não deu para esvaziar.'); }
  };

  // Busca do topo: filtra cadernos pelo nome, sem acento.
  const filtrarCadernos = (lista: any[]) => {
    const q = semAcento(busca);
    if (!q) return lista;
    return (lista || []).filter((c) => semAcento(c.name).includes(q));
  };

  const filtrarTags = (lista: any[]) => {
    const q = semAcento(busca);
    if (!q) return lista;
    return (lista || []).filter((t) => semAcento(t.name).includes(q));
  };

  const notasFiltradas = useMemo(() => {
    const q = semAcento(busca);
    let lista = notas;
    if (tipoFiltro !== 'tudo') {
      // "foto" não é um `type` — é a presença do arquivo. Mesma regra da
      // tela antiga, para os chips continuarem significando a mesma coisa.
      lista = lista.filter((n) => (tipoFiltro === 'foto' ? !!n.photo : n.type === tipoFiltro));
    }
    if (q) {
      lista = lista.filter((n) => semAcento(n.text).includes(q)
        || semAcento(n.title).includes(q) || semAcento(n.bookTitle).includes(q));
    }
    const ordenada = [...lista];
    if (ordem === 'pagina') {
      // Nota sem página vai para o fim: ela não tem lugar na espinha do livro.
      ordenada.sort((a, b) => (a.page ?? 1e9) - (b.page ?? 1e9));
    } else if (ordem === 'titulo') {
      ordenada.sort((a, b) => semAcento(a.title || a.text).localeCompare(semAcento(b.title || b.text)));
    } else {
      ordenada.sort((a, b) => String(b.createdAt).localeCompare(String(a.createdAt)));
    }
    return ordenada;
  }, [notas, busca, ordem, tipoFiltro]);

  // O editor precisa existir nos DOIS retornos desta tela. Montado só no do
  // início, ele nunca aparecia: o card que o abre está na vista interna, e
  // ali o JSX nem era renderizado — o clique setava o estado e nada acontecia.
  const editorMontado = editando ? (
    <NotaEditor
      session={session}
      nota={editando}
      onFechar={() => setEditando(null)}
      onSalvo={(r: any) => {
        // Atualiza a nota na lista sem refetch: ela é a única que mudou, e
        // recarregar tudo piscaria a tela inteira.
        setNotas((lista) => lista.map((x) => (x.id === r.id
          ? { ...x, title: r.title, text: r.text, pinned: r.pinned, memorize: r.memorize }
          : x)));
        setAchadas((lista) => lista && lista.map((x) => (x.id === r.id
          ? { ...x, title: r.title, text: r.text, pinned: r.pinned, memorize: r.memorize }
          : x)));
        carregar();
      }}
    />
  ) : null;

  const topo = (titulo: string, voltar: () => void) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 20px 8px' }}>
      <button onClick={voltar} aria-label="Voltar" style={{
        width: 34, height: 34, borderRadius: '50%', background: 'var(--paper-2)',
        border: '1px solid var(--line)', display: 'flex', alignItems: 'center', justifyContent: 'center',
        cursor: 'pointer',
      }}><Icon name="chevron-left" size={16} color="var(--ink-2)" /></button>
      <div className="serif" style={{ fontSize: 22, color: 'var(--ink)' }}>{titulo}</div>
    </div>
  );

  // A folha cobre a tela inteira, entao vem antes de qualquer outro retorno.
  if (folha) {
    return (
      <CadernoFolha
        session={session}
        caderno={folha}
        onVoltar={() => { setFolha(null); carregar(); }}
      />
    );
  }

  if (carregando) {
    return <div className="scroll" style={{ padding: 20, color: 'var(--ink-3)' }}>Abrindo seus cadernos…</div>;
  }

  if (dados && dados.disponivel === false) {
    return (
      <div className="scroll" style={{ paddingBottom: 110 }}>
        {topo('Cadernos', onBack)}
        <div style={{ padding: '0 20px' }}>
          <div style={{ ...CARD, padding: 22 }}>
            <div className="mono" style={EYEBROW}>Ainda não neste servidor</div>
            <div className="serif" style={{ fontSize: 24, color: 'var(--ink)', margin: '10px 0 8px' }}>
              Os cadernos estão a caminho.
            </div>
            <p style={{ fontSize: 14.5, lineHeight: 1.55, color: 'var(--ink-2)', margin: 0 }}>
              Suas anotações continuam inteiras no caderno de cada livro. Esta
              página abre assim que a atualização chegar aqui.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ── DENTRO DE UMA VISTA (caderno, atalho ou lixeira) ────────────────
  if (vista.tipo !== 'inicio') {
    const naLixeira = vista.tipo === 'lixeira';
    return (
      <div className="scroll" style={{ paddingBottom: 110 }}>
        {topo(vista.titulo || 'Notas', () => { setVista({ tipo: 'inicio' }); setBusca(''); })}
        <div style={{ padding: '0 20px' }}>
          {/* A busca vive DENTRO da vista também. Sem isto o filtro existia no
              código e não tinha onde digitar: quem abria um caderno com 80
              notas só podia rolar. */}
          <input
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Buscar nestas notas…"
            style={{
              width: '100%', padding: '11px 13px', borderRadius: 13, marginBottom: 10,
              background: 'var(--paper-2)', border: '1px solid var(--line)',
              color: 'var(--ink)', fontSize: 14, fontFamily: 'inherit',
            }}
          />

          {!naLixeira && (
            <div style={{ display: 'flex', gap: 6, overflowX: 'auto', marginBottom: 12, paddingBottom: 2 }}>
              {FILTROS_TIPO.map((f) => {
                const on = tipoFiltro === f.key;
                return (
                  <button key={f.key} onClick={() => setTipoFiltro(f.key)} style={{
                    flexShrink: 0, padding: '6px 12px', borderRadius: 999, cursor: 'pointer',
                    fontSize: 12, fontWeight: on ? 700 : 500, fontFamily: 'inherit',
                    background: on ? 'var(--ember-soft)' : 'var(--paper-2)',
                    border: `1px solid ${on ? 'var(--ember)' : 'var(--line)'}`,
                    color: on ? 'var(--ember-2)' : 'var(--ink-3)',
                  }}>{f.label}</button>
                );
              })}
              <span style={{ width: 1, background: 'var(--line)', margin: '3px 4px', flexShrink: 0 }} />
              {ORDENS.map((o) => {
                const on = ordem === o.key;
                return (
                  <button key={o.key} onClick={() => setOrdem(o.key as any)} style={{
                    flexShrink: 0, padding: '6px 12px', borderRadius: 999, cursor: 'pointer',
                    fontSize: 12, fontWeight: on ? 700 : 500, fontFamily: 'inherit',
                    background: 'transparent',
                    border: `1px solid ${on ? 'var(--line-strong)' : 'var(--line)'}`,
                    color: on ? 'var(--ink)' : 'var(--ink-4)',
                  }}>{o.label}</button>
                );
              })}
            </div>
          )}
          {naLixeira && notas.length > 0 && (
            <div style={{ ...CARD, padding: '12px 14px', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 10 }}>
              {/* O NÚMERO VEM DO SERVIDOR, não daqui.
                  Esta frase já prometeu "30 dias" enquanto nada acontecia,
                  porque o prazo era um número escrito na tela sem ninguém que
                  o cumprisse. Agora há varredura, e ela lê a mesma constante
                  que alimenta este texto. */}
              <div style={{ flex: 1, fontSize: 12.5, color: 'var(--ink-3)', lineHeight: 1.5 }}>
                O que está aqui volta a qualquer momento. Depois de {diasLixeira} dias, some de vez.
              </div>
              <button onClick={esvaziar} style={{
                padding: '8px 12px', borderRadius: 999, background: 'transparent',
                border: '1px solid var(--line-strong)', color: 'var(--ink-3)', fontSize: 12.5, cursor: 'pointer',
              }}>Esvaziar</button>
            </div>
          )}

          {carregandoNotas ? (
            <div style={{ color: 'var(--ink-3)', fontSize: 14, padding: '20px 0' }}>Carregando…</div>
          ) : notasFiltradas.length === 0 ? (
            <div style={{ ...CARD, padding: 22, textAlign: 'center' }}>
              <div className="serif" style={{ fontSize: 20, color: 'var(--ink)', marginBottom: 6 }}>
                {naLixeira ? 'A lixeira está vazia' : 'Nenhuma nota aqui ainda'}
              </div>
              <p style={{ fontSize: 13.5, color: 'var(--ink-3)', margin: 0, lineHeight: 1.5 }}>
                {naLixeira ? 'Nada foi apagado ainda.' : 'Anote pelo caderno do livro ou pela captura rápida.'}
              </p>
            </div>
          ) : (
            /* O MESMO CARD DAS NOTAS DO LIVRO.
               Esta lista desenhava um card próprio: o áudio não tocava, a foto
               não aparecia, o grifo não ficava grifo e a cor da nota sumia.
               Duas telas mostrando a mesma coisa de dois jeitos é como se chega
               a dois apps dentro do mesmo app — e foi assim que o `NoteCard`
               nasceu, quando a visão global copiou o card da tela do livro.

               Agora é o componente de sempre, o mesmo de "Estudos" e do caderno
               do livro. A busca acende o trecho porque o realce passou a morar
               dentro dele. */
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {notasFiltradas.map((n) => (
                <div key={n.id}>
                  {/* De que livro veio. Aqui as notas vêm de livros diferentes,
                      então o título precede o card — é o mesmo arranjo da tela
                      de Estudos. */}
                  {n.bookTitle && (
                    <button
                      onClick={() => { if (onAbrirLivro && n.bookId) onAbrirLivro(n.bookId, n.page ?? null); }}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 7, marginBottom: 5,
                        background: 'none', border: 'none', padding: 0, cursor: 'pointer', maxWidth: '100%',
                      }}>
                      <Icon name="book" size={11} color="var(--ink-4)" />
                      <span style={{
                        fontSize: 11.5, color: 'var(--ink-3)', overflow: 'hidden',
                        textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>{n.bookTitle}</span>
                    </button>
                  )}
                  <NoteCard
                    n={n}
                    justAdded={false}
                    busca={busca}
                    // Na lixeira o card não abre para editar: o caminho de lá
                    // é restaurar, e só.
                    onEdit={() => { if (!naLixeira) setEditando(n); }}
                    pageLabel={makePageLabel(n.bookUnit)}
                    livro={n.bookTitle ? { titulo: n.bookTitle, autor: n.bookAuthor, capaUrl: n.bookCoverUrl } : null}
                  />
                  {naLixeira && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 6 }}>
                      <button onClick={() => restaurar(n)} style={{
                        padding: '6px 12px', borderRadius: 999,
                        background: 'transparent', border: '1px solid var(--ember)',
                        color: 'var(--ember)', fontSize: 12, cursor: 'pointer',
                      }}>Restaurar</button>
                      {/* QUANTO FALTA, nota por nota. Um prazo coletivo no topo
                          da tela não responde a pergunta que a pessoa tem
                          diante de UMA nota: "esta aqui, quando some?" */}
                      <span className="mono" style={{ fontSize: 10, color: 'var(--ink-4)' }}>
                        {(() => {
                          const dias = Math.ceil(
                            (new Date(n.deletedAt).getTime() + diasLixeira * 86400000 - Date.now()) / 86400000
                          );
                          if (dias <= 0) return 'SOME A QUALQUER MOMENTO';
                          return dias === 1 ? 'SOME AMANHÃ' : `SOME EM ${dias} DIAS`;
                        })()}
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* A CAPTURA DENTRO DO CADERNO.
              Sem ela o caderno pessoal nascia vazio e sem saída: herdava a
              lista de notas do caderno de livro e não herdava a forma de
              escrever, porque o compositor está fundido na tela do livro.
              Só aparece num caderno de verdade — "Fixadas" e "Para memorizar"
              são recortes, não lugares onde uma nota nasce. */}
          {vista.tipo === 'caderno' && vista.id && (
            <>
              <BarraDeCaptura onAbrir={(m, t) => { setNovoModo(m); setNovoTexto(t || ''); setNovoAberto(true); }} />
              <NotaComposer
                session={session}
                notebookId={vista.id}
                nomeDoCaderno={vista.titulo}
                aberto={novoAberto}
                modoInicial={novoModo}
                textoInicial={novoTexto}
                onFechar={() => setNovoAberto(false)}
                onCriada={() => { abrirVista(vista); carregar(); }}
              />
            </>
          )}
        </div>
        {editorMontado}
      </div>
    );
  }

  // ── INÍCIO ──────────────────────────────────────────────────────────
  const at = dados?.atalhos || { todas: 0, entrada: 0, fixadas: 0, memorizar: 0, lixeira: 0 };
  // O prazo da lixeira é do servidor: é ele que a varredura obedece.
  const diasLixeira = dados?.lixeiraDias || 7;
  const pessoais = filtrarCadernos(dados?.cadernos || []);
  const deLivro = filtrarCadernos(dados?.livros || []);

  return (
    <div className="scroll" style={{ paddingBottom: 110 }}>
      {topo('Cadernos', onBack)}

      <div style={{ padding: '0 20px 14px' }}>
        <input
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          placeholder="Buscar em cadernos e notas…"
          style={{
            width: '100%', padding: '12px 14px', borderRadius: 14,
            background: 'var(--paper-2)', border: '1px solid var(--line)',
            color: 'var(--ink)', fontSize: 14, fontFamily: 'inherit',
          }}
        />
      </div>

      {erro && (
        <div style={{ padding: '0 20px 12px' }}>
          <div style={{ ...CARD, borderColor: 'var(--ember)', padding: '11px 13px', fontSize: 13, color: 'var(--ink-2)' }}>
            {erro}
          </div>
        </div>
      )}

      <div style={{ padding: '0 16px', display: 'flex', alignItems: 'stretch', gap: 6 }}>
        <Atalho icone="library" rotulo="Todas" nome="Todas as notas" total={at.todas}
          onClick={() => abrirVista({ tipo: 'todas', titulo: 'Todas as notas' })} />
        {/* A CAIXA DE ENTRADA SÓ APARECE QUANDO TEM ALGO DENTRO.
            Ela é o destino de nota SEM LUGAR — e nenhuma nota nasce sem lugar:
            anotação de livro cai no caderno do livro, escrita em caderno
            pessoal vira página. Sobram dois casos raros: nota órfã de código
            antigo e nota de caderno apagado.
            Na prática, em 24/09/2026, ela estava em ZERO para todos os cinco
            usuários reais, desde sempre — um quinto da faixa de atalhos gasto
            num lugar que nunca teve nada. Nada se perde: o caderno continua
            existindo como destino, e no dia em que receber algo, reaparece. */}
        {at.entrada > 0 && (
          <Atalho icone="mail" rotulo="Entrada" nome="Caixa de entrada" total={at.entrada}
            onClick={() => dados?.entrada && abrirVista({ tipo: 'caderno', id: dados.entrada.id, titulo: 'Caixa de entrada' })} />
        )}
        <Atalho icone="star" rotulo="Fixadas" nome="Fixadas" total={at.fixadas}
          onClick={() => abrirVista({ tipo: 'fixadas', titulo: 'Fixadas' })} />
        <Atalho icone="bookmark" rotulo="Memorizar" nome="Para memorizar" total={at.memorizar}
          onClick={() => abrirVista({ tipo: 'memorizar', titulo: 'Para memorizar' })} />
        <Atalho icone="trash" rotulo="Lixeira" nome="Lixeira" total={at.lixeira}
          onClick={() => abrirVista({ tipo: 'lixeira', titulo: 'Lixeira' })} />
      </div>

      {achadas !== null && (
        <div style={{ padding: '22px 20px 0' }}>
          <div className="mono" style={{ ...EYEBROW, marginBottom: 10 }}>
            {achadas.length === 0 ? 'Nenhuma nota com isso' : achadas.length === 1 ? '1 nota' : `${achadas.length} notas`}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {achadas.slice(0, 30).map((n) => (
              <div key={n.id}>
                {n.bookTitle && (
                  <button
                    onClick={() => { if (onAbrirLivro && n.bookId) onAbrirLivro(n.bookId, n.page ?? null); }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 7, marginBottom: 5,
                      background: 'none', border: 'none', padding: 0, cursor: 'pointer', maxWidth: '100%',
                    }}>
                    <Icon name="book" size={11} color="var(--ink-4)" />
                    <span style={{ fontSize: 11.5, color: 'var(--ink-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{n.bookTitle}</span>
                  </button>
                )}
                <NoteCard
                  n={n}
                  justAdded={false}
                  busca={busca}
                  onEdit={() => setEditando(n)}
                  pageLabel={makePageLabel(n.bookUnit)}
                  livro={n.bookTitle ? { titulo: n.bookTitle, autor: n.bookAuthor, capaUrl: n.bookCoverUrl } : null}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Cadernos pessoais ─────────────────────────────────────── */}
      <div style={{ padding: '26px 20px 0' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 6 }}>
          <span className="mono" style={EYEBROW}>Seus cadernos</span>
          <button onClick={() => setCriando((v) => !v)} style={{
            marginLeft: 'auto', background: 'transparent', border: 'none', padding: 4,
            color: 'var(--ember)', fontSize: 13, cursor: 'pointer', fontWeight: 600,
          }}>{criando ? 'Cancelar' : '+ Novo'}</button>
        </div>

        {criando && (
          <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
            <input
              value={nomeNovo}
              onChange={(e) => setNomeNovo(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') criarCaderno(); }}
              placeholder="Ex.: Estudo de Romanos"
              autoFocus
              style={{
                flex: 1, padding: '11px 13px', borderRadius: 13,
                background: 'var(--paper-2)', border: '1px solid var(--line)',
                color: 'var(--ink)', fontSize: 14, fontFamily: 'inherit',
              }}
            />
            <button onClick={criarCaderno} style={{
              padding: '11px 16px', borderRadius: 13, border: 'none',
              background: 'var(--ember)', color: 'var(--paper)', fontSize: 14, fontWeight: 600, cursor: 'pointer',
            }}>Criar</button>
          </div>
        )}

        {pessoais.length === 0 ? (
          <p style={{ fontSize: 13.5, color: 'var(--ink-3)', lineHeight: 1.55, margin: '4px 0 0' }}>
            Um caderno junta notas de vários livros — "Sermões", "Teologia", o
            estudo que você está fazendo agora.
          </p>
        ) : (
          <div>{pessoais.map((c: any) => (
            <LinhaCaderno key={c.id} c={c}
              onAbrir={() => setFolha(c)}
              onMenu={() => setMenuDe(c)} />
          ))}</div>
        )}
      </div>

      {/* ── Etiquetas ─────────────────────────────────────────────── */}
      {tags.length > 0 && (
        <div style={{ padding: '26px 20px 0' }}>
          <span className="mono" style={EYEBROW}>Etiquetas</span>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, marginTop: 8 }}>
            {filtrarTags(tags).map((t: any) => (
              <button key={t.id}
                onClick={() => abrirVista({ tipo: 'tag', id: t.id, titulo: '#' + t.name })}
                onContextMenu={(e) => { e.preventDefault(); setMenuTag(t); setNomeTag(t.name); }}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '7px 12px', borderRadius: 999, cursor: 'pointer', fontFamily: 'inherit',
                  background: 'var(--paper-2)', border: '1px solid var(--line)',
                  color: 'var(--ink-2)', fontSize: 13,
                }}>
                #{t.name}
                <span className="mono" style={{ fontSize: 10.5, color: 'var(--ink-4)' }}>{t.total}</span>
                <span onClick={(e) => { e.stopPropagation(); setMenuTag(t); setNomeTag(t.name); }}
                  style={{ marginLeft: 2, color: 'var(--ink-4)', padding: '0 2px' }}>⋯</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ── Cadernos de livros ────────────────────────────────────────
          ATALHO, não uma segunda tela.

          O caderno de um livro JÁ EXISTE: é a página de estudos dele, com a
          espinha da leitura, a âncora de página e o compositor com livro. Ao
          abrir aqui uma lista genérica, eu tinha desenhado uma segunda versão
          da mesma coisa — com layout diferente, o que é pior do que não ter.

          Então a linha daqui leva para lá. Esta página organiza e encontra;
          quem desenha o caderno do livro é a tela do livro. */}
      {deLivro.length > 0 && (
        <div style={{ padding: '26px 20px 0' }}>
          <span className="mono" style={EYEBROW}>Por livro</span>
          <div style={{ marginTop: 6 }}>
            {(verTodosLivros ? deLivro : deLivro.slice(0, 6)).map((c: any) => (
              <LinhaCaderno key={c.id} c={c}
                onAbrir={() => { if (onAbrirLivro && c.bookId) onAbrirLivro(c.bookId, null); }} />
            ))}
            {deLivro.length > 6 && !verTodosLivros && (
              <button onClick={() => setVerTodosLivros(true)} style={{
                marginTop: 10, background: 'transparent', border: 'none', padding: 4,
                color: 'var(--ember)', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
              }}>Ver todos os {deLivro.length} livros</button>
            )}
          </div>
        </div>
      )}

      {/* ── Etiqueta: renomear ou apagar ──────────────────────────── */}
      {menuTag && (
        <div onClick={() => setMenuTag(null)} style={{
          position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,.5)',
          display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
        }}>
          <div onClick={(e) => e.stopPropagation()} style={{
            width: '100%', maxWidth: 520, background: 'var(--paper)',
            borderRadius: '26px 26px 0 0', padding: '18px 22px 30px',
            borderTop: '1px solid var(--line-strong)',
          }}>
            <div className="serif" style={{ fontSize: 20, color: 'var(--ink)', marginBottom: 14 }}>#{menuTag.name}</div>
            <input
              value={nomeTag}
              onChange={(e) => setNomeTag(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') renomearTag(); }}
              autoFocus
              style={{
                width: '100%', padding: '12px 14px', borderRadius: 13, marginBottom: 12,
                background: 'var(--paper-2)', border: '1px solid var(--line)',
                color: 'var(--ink)', fontSize: 14, fontFamily: 'inherit',
              }}
            />
            <button onClick={renomearTag} style={{
              width: '100%', padding: '13px 0', borderRadius: 13, border: 'none',
              background: 'var(--ember)', color: 'var(--paper)', fontSize: 14.5,
              fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', marginBottom: 8,
            }}>Renomear</button>
            <button onClick={() => apagarTag(menuTag)} style={{ ...botaoSheet, color: 'var(--ember)' }}>
              Apagar a etiqueta
            </button>
            <div style={{ fontSize: 12, color: 'var(--ink-4)', lineHeight: 1.5, marginTop: 8 }}>
              Apagar tira a etiqueta da lista e das notas. O <code>#{menuTag.name}</code> escrito no texto continua lá.
            </div>
          </div>
        </div>
      )}

      {editorMontado}

      {/* ── Renomear ──────────────────────────────────────────────── */}
      {renomeando && (
        <div onClick={() => setRenomeando(null)} style={{
          position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,.5)',
          display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
        }}>
          <div onClick={(e) => e.stopPropagation()} style={{
            width: '100%', maxWidth: 520, background: 'var(--paper)',
            borderRadius: '26px 26px 0 0', padding: '18px 22px 30px',
            borderTop: '1px solid var(--line-strong)',
          }}>
            <div className="serif" style={{ fontSize: 20, color: 'var(--ink)', marginBottom: 14 }}>Renomear caderno</div>
            <input
              value={nomeEdit}
              onChange={(e) => setNomeEdit(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') renomear(); }}
              autoFocus
              style={{
                width: '100%', padding: '12px 14px', borderRadius: 13, marginBottom: 12,
                background: 'var(--paper-2)', border: '1px solid var(--line)',
                color: 'var(--ink)', fontSize: 14, fontFamily: 'inherit',
              }}
            />
            <button onClick={renomear} style={{
              width: '100%', padding: '13px 0', borderRadius: 13, border: 'none',
              background: 'var(--ember)', color: 'var(--paper)', fontSize: 14.5,
              fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
            }}>Salvar</button>
          </div>
        </div>
      )}

      {/* ── Menu de um caderno pessoal ────────────────────────────── */}
      {menuDe && (
        <div onClick={() => setMenuDe(null)} style={{
          position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,.5)',
          display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
        }}>
          <div onClick={(e) => e.stopPropagation()} style={{
            width: '100%', maxWidth: 520, background: 'var(--paper)',
            borderRadius: '26px 26px 0 0', padding: '18px 22px 30px',
            borderTop: '1px solid var(--line-strong)',
          }}>
            <div className="serif" style={{ fontSize: 20, color: 'var(--ink)', marginBottom: 14 }}>{menuDe.name}</div>
            <button onClick={() => { setRenomeando(menuDe); setNomeEdit(menuDe.name); setMenuDe(null); }} style={botaoSheet}>
              Renomear
            </button>
            <button onClick={() => fixar(menuDe)} style={botaoSheet}>
              {menuDe.pinned ? 'Desafixar' : 'Fixar no topo'}
            </button>
            <button onClick={() => apagar(menuDe, 'entrada')} style={botaoSheet}>
              Apagar e mover as notas para a Caixa de entrada
            </button>
            <button onClick={() => apagar(menuDe, 'lixeira')} style={{ ...botaoSheet, color: 'var(--ember)' }}>
              Apagar com as notas
            </button>
            <button onClick={() => setMenuDe(null)} style={{ ...botaoSheet, color: 'var(--ink-4)' }}>Cancelar</button>
          </div>
        </div>
      )}
    </div>
  );
};

const botaoSheet: React.CSSProperties = {
  width: '100%', padding: '14px 0', background: 'transparent', border: 'none',
  borderBottom: '1px solid var(--line)', color: 'var(--ink)', fontSize: 14.5,
  textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit',
};

export default CadernosScreen;
